import { createTheme } from '@mui/material/styles';

const getDesignTokens = (mode) => {
  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main: '#0066FF',
        dark: '#0052CC',
        light: '#388BFF',
        contrastText: '#FFFFFF',
      },
      error: { main: '#EF4444' },
      warning: { main: '#F59E0B' },
      success: { main: '#10B981' },
      info: { main: '#0284C7' },

      background: isDark
        ? {
            default: '#0B0F19',
            paper: '#131C2E',
            secondary: '#182235',
            tertiary: '#1E293B',
          }
        : {
            default: '#F8FAFC',
            paper: '#FFFFFF',
            secondary: '#F1F5F9',
            tertiary: '#E2E8F0',
          },

      text: isDark
        ? {
            primary: '#F8FAFC',
            secondary: '#94A3B8',
            tertiary: '#64748B',
            disabled: '#475569',
          }
        : {
            primary: '#0F172A',
            secondary: '#475569',
            tertiary: '#94A3B8',
            disabled: '#CBD5E1',
          },

      action: isDark
        ? {
            disabledBackground: 'rgba(255, 255, 255, 0.08)',
            disabled: 'rgba(255, 255, 255, 0.35)',
            hover: 'rgba(255, 255, 255, 0.06)',
            selected: 'rgba(0, 102, 255, 0.16)',
          }
        : {
            disabledBackground: 'rgba(0, 0, 0, 0.06)',
            disabled: 'rgba(0, 0, 0, 0.35)',
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 102, 255, 0.08)',
          },

      divider: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 42, 0.08)',

      button: {
        main: '#0066FF',
        hover: '#0052CC',
        active: '#003D99',
        disabled: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)',
        contrastText: '#FFFFFF',
        transparent: {
          color: isDark ? '#388BFF' : '#0066FF',
          hoverColor: isDark ? '#60A5FA' : '#0052CC',
          activeColor: isDark ? '#2563EB' : '#003D99',
          disabledColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(15, 23, 42, 0.35)',
        },
      },

      shadows: {
        text: isDark ? '0 1px 2px rgba(0,0,0,0.6)' : '0 1px 2px rgba(0,0,0,0.06)',
        card: isDark
          ? '0 10px 30px -5px rgba(0,0,0,0.5), 0 2px 8px -2px rgba(0,0,0,0.4)'
          : '0 10px 30px -5px rgba(15, 23, 42, 0.06), 0 2px 6px -1px rgba(15, 23, 42, 0.03)',
        elevated: isDark
          ? '0 20px 40px -10px rgba(0,0,0,0.6)'
          : '0 20px 40px -10px rgba(15, 23, 42, 0.08)',
      },

      placeholder: {
        text: isDark ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.6)',
      },

      scrollbar: {
        track: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)',
        thumb: isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(15, 23, 42, 0.16)',
        thumbHover: isDark ? 'rgba(255, 255, 255, 0.32)' : 'rgba(15, 23, 42, 0.28)',
      },

      input: {
        background: isDark ? '#131C2E' : '#FFFFFF',
        activeBackground: isDark ? '#182235' : '#F8FAFC',
        border: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.14)',
        activeBorder: '#0066FF',
      },

      separator: {
        background: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.1)',
        icon: isDark ? '#94A3B8' : '#64748B',
        hover: '#0066FF',
      },

      header: {
        background: isDark ? 'rgba(11, 15, 25, 0.82)' : 'rgba(255, 255, 255, 0.85)',
        textColor: isDark ? '#F8FAFC' : '#0F172A',
      },

      hero: {
        background: isDark ? '#0B0F19' : '#F8FAFC',
      },

      footer: {
        background: isDark ? '#0B0F19' : '#F8FAFC',
        text: isDark ? '#94A3B8' : '#64748B',
      },

      console: {
        background: isDark ? '#131C2E' : '#FFFFFF',
        hoverBackground: isDark ? '#182235' : '#F8FAFC',
        border: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 42, 0.08)',
      },

      auth: {
        background: isDark ? '#0B0F19' : '#F8FAFC',
        formBackground: isDark ? '#131C2E' : '#FFFFFF',
        inputBackground: isDark ? '#182235' : '#FFFFFF',
        border: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 42, 0.08)',
      },

      table: {
        border: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 42, 0.08)',
        headerBg: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.03)',
        resizer: {
          default: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.1)',
          hover: '#0066FF',
          active: '#0052CC',
        },
      },
    },

    typography: {
      fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: {
        fontWeight: 800,
        letterSpacing: '-0.03em',
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.025em',
      },
      h3: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.015em',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 500,
      },
      subtitle2: {
        fontWeight: 600,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },

    shape: {
      borderRadius: 14,
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          containedPrimary: {
            boxShadow: '0 4px 14px 0 rgba(0, 102, 255, 0.25)',
            '&:hover': {
              boxShadow: '0 6px 20px 0 rgba(0, 102, 255, 0.35)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },
  };
};

export const getTheme = (mode) => createTheme(getDesignTokens(mode));